import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { usePreviousWizardPath } from "@/hooks/usePreviousWizardPath";

/**
 * Props for the NumberPortingNavigation component.
 */
export type NumberPortingNavigationProps = {
  /** If true, we are in the 'missing information' flow (Portal). */
  missingInfo: boolean | undefined;
  /** Whether the 'Complete' or 'Forward' button should be disabled. */
  completeButtonDisabled: boolean;
  /** Whether the porting data is being submitted - shows a spinner on the primary button. */
  isSubmitting: boolean;
  /** User's choice regarding number porting ("yes" or "no"). */
  enableNumberPortingValue: "yes" | "no" | undefined;
  /** Callback for moving forward in the Onboarding flow. */
  onForward: () => void;
  /** Callback for completing the MNP data in the Portal flow. */
  onComplete: () => void;
  /** Function to show an error if no porting choice was made. */
  setShowNumberPortingError: (show: boolean) => void;
};

/**
 * Renders the navigation buttons (Back, Forward, Complete) for the Number Porting step.
 * Adapts its layout and labels based on whether it's part of the standard Onboarding or the Portal 'missing info' flow.
 */
export const NumberPortingNavigation = ({
  missingInfo,
  completeButtonDisabled,
  isSubmitting,
  enableNumberPortingValue,
  onForward,
  onComplete,
  setShowNumberPortingError,
}: NumberPortingNavigationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const previousPath = usePreviousWizardPath(3);

  const handleBack = () => {
    if (!previousPath) {
      return;
    }

    navigate(previousPath);
  };

  // In the 'missing info' flow there is nothing to go back to, only a submit.
  if (missingInfo) {
    return (
      <StepFlowNavigation
        primaryButton={
          <Button
            variant="primary"
            disabled={completeButtonDisabled}
            loading={isSubmitting}
            onClick={onComplete}
          >
            {t("Common.label.complete")}
          </Button>
        }
      />
    );
  }

  return (
    <StepFlowNavigation
      primaryButton={
        <Button
          variant="primary"
          disabled={completeButtonDisabled}
          loading={isSubmitting}
          onClick={() => {
            // Ensure a choice (Yes/No) was made before proceeding.
            if (enableNumberPortingValue) {
              onForward();

              return;
            }

            setShowNumberPortingError(true);
          }}
        >
          {t("Common.label.forward")}
        </Button>
      }
      secondaryButton={
        <Button
          variant="secondary"
          disabled={!previousPath}
          onClick={handleBack}
        >
          {t("Common.label.back")}
        </Button>
      }
    />
  );
};
